import { useQuery } from "@tanstack/react-query";
import { Shield, Users, FileText, Clock, Database, CheckCircle2, XCircle, RefreshCw, AlertTriangle, BookOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AdminLayout, { AdminPageHeader } from "./layout";

interface AuditLogEntry {
  id: number;
  actorUserId: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  ipAddress: string | null;
  createdAt: string;
}

interface AdminAccount {
  userId: string;
  adminSlot: number;
  promotedBy: string | null;
  createdAt: string;
}

interface SchemaHealthResult {
  pass: boolean;
  checkedTables: number;
  totalTables: number;
  checkedColumns: number;
  errors: string[];
  ranAt: string;
}

interface StaleItem {
  id: number;
  name?: string;
  title?: string;
  lastReviewedAt?: string | null;
  reviewCycleMonths?: number | null;
  updatedAt?: string;
  createdAt?: string;
}

interface StaleContentResult {
  conditions: StaleItem[];
  guidelines: StaleItem[];
  medications: StaleItem[];
  resources: StaleItem[];
  scenarios: StaleItem[];
  modules: StaleItem[];
  totalStale: number;
  checkedAt: string;
}

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function AdminDashboard() {
  const { data: accounts = [], isLoading: accountsLoading } = useQuery<AdminAccount[]>({
    queryKey: ["admin", "accounts"],
    queryFn: async () => {
      const res = await fetch(`${basePath}/api/admin/accounts`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch admin accounts");
      return res.json() as Promise<AdminAccount[]>;
    },
  });

  const { data: auditLog = [], isLoading: auditLoading } = useQuery<AuditLogEntry[]>({
    queryKey: ["admin", "audit-log"],
    queryFn: async () => {
      const res = await fetch(`${basePath}/api/admin/audit-log?limit=20`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch audit log");
      return res.json() as Promise<AuditLogEntry[]>;
    },
  });

  const {
    data: schemaHealth,
    isLoading: schemaHealthLoading,
    isFetching: schemaHealthFetching,
    refetch: refetchSchemaHealth,
  } = useQuery<SchemaHealthResult>({
    queryKey: ["admin", "schema-health"],
    queryFn: async () => {
      const res = await fetch(`${basePath}/api/admin/schema-health`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch schema health");
      return res.json() as Promise<SchemaHealthResult>;
    },
    staleTime: 60_000,
  });

  const {
    data: staleContent,
    isLoading: staleLoading,
    isFetching: staleFetching,
    refetch: refetchStale,
  } = useQuery<StaleContentResult>({
    queryKey: ["admin", "content-stale"],
    queryFn: async () => {
      const res = await fetch(`${basePath}/api/admin/content/stale`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch content staleness");
      return res.json() as Promise<StaleContentResult>;
    },
    staleTime: 120_000,
  });

  return (
    <AdminLayout>
      <AdminPageHeader title="Dashboard" description="Content health, admin accounts, and audit log" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Admin Accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">
              {accountsLoading ? "—" : `${accounts.length} / 2`}
            </p>
            <p className="text-xs text-slate-500 mt-1">Maximum 2 seats</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Content Freshness
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-3xl font-bold ${staleContent && staleContent.totalStale > 0 ? "text-amber-600" : "text-slate-900"}`}>
              {staleLoading ? "—" : staleContent ? staleContent.totalStale : "?"}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {staleContent && staleContent.totalStale > 0
                ? "Items past review cycle"
                : "All content current"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Session Timeout
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">15 min</p>
            <p className="text-xs text-slate-500 mt-1">Inactivity limit</p>
          </CardContent>
        </Card>
      </div>

      {/* Content Freshness Detail */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-teal-700" />
            Content Freshness
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refetchStale()}
            disabled={staleFetching}
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-800"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${staleFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {staleLoading ? (
            <p className="text-sm text-slate-500">Checking content freshness…</p>
          ) : !staleContent ? (
            <p className="text-sm text-slate-500">Failed to load content status.</p>
          ) : staleContent.totalStale === 0 ? (
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-50 border-green-200">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">All content up to date</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Checked {new Date(staleContent.checkedAt).toLocaleString("en-GB")}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {staleContent.totalStale} item{staleContent.totalStale !== 1 ? "s" : ""} past review cycle
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Checked {new Date(staleContent.checkedAt).toLocaleString("en-GB")}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { label: "Conditions", count: staleContent.conditions.length, color: "bg-blue-50 border-blue-200 text-blue-800" },
                  { label: "Guidelines", count: staleContent.guidelines.length, color: "bg-purple-50 border-purple-200 text-purple-800" },
                  { label: "Medications", count: staleContent.medications.length, color: "bg-red-50 border-red-200 text-red-800" },
                  { label: "Resources", count: staleContent.resources.length, color: "bg-green-50 border-green-200 text-green-800" },
                  { label: "Scenarios", count: staleContent.scenarios.length, color: "bg-yellow-50 border-yellow-200 text-yellow-800" },
                  { label: "Modules", count: staleContent.modules.length, color: "bg-indigo-50 border-indigo-200 text-indigo-800" },
                ].map(({ label, count, color }) => (
                  <div key={label} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${count > 0 ? color : "bg-slate-50 border-slate-200 text-slate-500"}`}>
                    <span className="text-xs font-medium">{label}</span>
                    <Badge variant={count > 0 ? "destructive" : "secondary"} className="text-xs">
                      {count}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4 text-teal-700" />
            Schema Health
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refetchSchemaHealth()}
            disabled={schemaHealthFetching}
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-800"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${schemaHealthFetching ? "animate-spin" : ""}`} />
            Recheck
          </Button>
        </CardHeader>
        <CardContent>
          {schemaHealthLoading ? (
            <p className="text-sm text-slate-500">Running drift check…</p>
          ) : !schemaHealth ? (
            <p className="text-sm text-slate-500">Failed to load schema health.</p>
          ) : (
            <div className="space-y-3">
              <div className={`flex items-center gap-3 p-3 rounded-lg border ${schemaHealth.pass ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                {schemaHealth.pass ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${schemaHealth.pass ? "text-green-800" : "text-red-800"}`}>
                    {schemaHealth.pass ? "No schema drift detected" : `${schemaHealth.errors.length} issue${schemaHealth.errors.length === 1 ? "" : "s"} detected`}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {schemaHealth.checkedTables}/{schemaHealth.totalTables} tables · {schemaHealth.checkedColumns} columns checked · {new Date(schemaHealth.ranAt).toLocaleString("en-GB")}
                  </p>
                </div>
                <Badge variant={schemaHealth.pass ? "secondary" : "destructive"} className="shrink-0">
                  {schemaHealth.pass ? "PASS" : "FAIL"}
                </Badge>
              </div>
              {schemaHealth.errors.length > 0 && (
                <div className="space-y-1.5">
                  {schemaHealth.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 px-3 py-2 bg-red-50 rounded border border-red-100">
                      <XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                      <p className="text-xs font-mono text-red-800 break-all">{err}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-teal-700" />
            Admin Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {accountsLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-slate-500">No admin accounts found.</p>
          ) : (
            <div className="space-y-3">
              {accounts.map((account) => (
                <div
                  key={account.userId}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <div>
                    <p className="text-sm font-mono text-slate-800">{account.userId}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Promoted by: {account.promotedBy ?? "unknown"} ·{" "}
                      {new Date(account.createdAt).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                  <Badge variant="secondary">Slot {account.adminSlot}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-teal-700" />
            Recent Audit Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {auditLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : auditLog.length === 0 ? (
            <p className="text-sm text-slate-500">No audit log entries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="pb-2 pr-4 font-medium text-slate-600">Action</th>
                    <th className="pb-2 pr-4 font-medium text-slate-600">Actor</th>
                    <th className="pb-2 pr-4 font-medium text-slate-600">Resource</th>
                    <th className="pb-2 font-medium text-slate-600">When</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((entry) => (
                    <tr key={entry.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs text-teal-800">{entry.action}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-slate-600 truncate max-w-[120px]">
                        {entry.actorUserId.slice(0, 16)}…
                      </td>
                      <td className="py-2 pr-4 text-xs text-slate-500">
                        {entry.resourceType ? `${entry.resourceType}${entry.resourceId ? ` #${entry.resourceId}` : ""}` : "—"}
                      </td>
                      <td className="py-2 text-xs text-slate-500">
                        {new Date(entry.createdAt).toLocaleString("en-GB")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

export default AdminDashboard;
